import { createFileRoute, Link } from "@tanstack/react-router";
import { makeSeoDescription } from "@/lib/seo-description";

const SITE = "https://lifewestway.com";
const TITLE = "Terms of Use | LifeWestWay";
const DESC =
  "Terms of Use governing your access to LifeWestWay — informational content, intellectual property, third-party links, AdSense advertising, limitation of liability, and your rights.";
const URL_ = `${SITE}/terms`;

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <header className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Legal</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Terms of Use
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">Last Updated: July 27, 2026</p>
      </header>

      <div className="space-y-6 text-[15px] leading-relaxed text-foreground/80">
        <p>
          Welcome to <strong className="text-foreground">LifeWestWay</strong> (&ldquo;we,&rdquo;
          &ldquo;us,&rdquo; &ldquo;our,&rdquo; or the &ldquo;Site&rdquo;). By accessing or using this
          Site, you agree to be bound by these Terms of Use (&ldquo;Terms&rdquo;). If you do not agree
          with these Terms, please do not use the Site.
        </p>

        {/* 1. About This Site */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">1. About This Site</h2>
        <p>
          LifeWestWay provides general informational content about immigration, visas, jobs, tax,
          finance, housing, and related topics for individuals considering a move to the United
          States, United Kingdom, Australia, Italy, Portugal, or Canada.
        </p>

        {/* 2. Not Professional Advice */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          2. Not Professional Advice
        </h2>
        <p>
          All content on this Site is provided for general informational purposes only and does not
          constitute legal, immigration, tax, or financial advice. See our full{" "}
          <Link title="Read the LifeWestWay disclaimer" to="/disclaimer" className="text-accent hover:underline">
            Disclaimer
          </Link>{" "}
          for details. Nothing on this Site should be treated as a substitute for advice from a
          licensed attorney, registered migration agent, accountant, or financial advisor.
        </p>

        {/* 3. Use of the Site */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">3. Use of the Site</h2>
        <p>You agree to use this Site only for lawful purposes. You agree not to:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            Use the Site in any way that violates applicable local, national, or international law
          </li>
          <li>
            Attempt to gain unauthorized access to any part of the Site, its servers, or any
            connected systems
          </li>
          <li>
            Use any automated system (bots, scrapers, spiders) to access the Site in a manner that
            sends more requests than a human could reasonably produce
          </li>
          <li>
            Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Site's content
            without our express written permission
          </li>
          <li>Introduce viruses, malware, or any other harmful material through the Site</li>
        </ul>

        {/* 4. Intellectual Property */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          4. Intellectual Property
        </h2>
        <p>
          All content on this Site — including text, articles, graphics, checklists, downloadable
          PDFs, logos, and design elements — is owned by or licensed to LifeWestWay and is protected
          by applicable copyright and intellectual property laws, unless otherwise stated.
        </p>
        <p>You may:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>View, download, and print content for personal, non-commercial use</li>
          <li>Share links to our articles on social media or via email</li>
        </ul>
        <p>You may not:</p>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            Republish, redistribute, or reproduce our content on another website or publication
            without prior written permission
          </li>
          <li>Modify, adapt, or create derivative works from our content for commercial purposes</li>
          <li>Remove any copyright, trademark, or proprietary notices from our content</li>
        </ul>

        {/* 5. Downloadable PDFs and Checklists */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          5. Downloadable PDFs and Checklists
        </h2>
        <p>
          Free PDF checklists and downloadable resources on this Site are provided for personal use.
          They may not be resold, redistributed commercially, or repackaged as your own original work.
        </p>

        {/* 6. Third-Party Links and Advertising */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          6. Third-Party Links and Advertising
        </h2>
        <p>
          This Site contains links to third-party websites, including official government resources,
          and displays advertisements served through Google AdSense and potentially other
          advertising networks.
        </p>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            We do not control, endorse, or take responsibility for the content, accuracy, or
            practices of any third-party site we link to.
          </li>
          <li>
            Advertisements displayed on this Site are served by third-party advertising companies and
            may use cookies as described in our{" "}
            <Link title="Read the LifeWestWay privacy policy" to="/privacy" className="text-accent hover:underline">
              Privacy Policy
            </Link>
            .
          </li>
          <li>We are not responsible for the products, services, or claims made by advertisers.</li>
        </ul>

        {/* 7. Accuracy of Information */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          7. Accuracy of Information
        </h2>
        <p>
          While we strive to keep information accurate and current, immigration rules, tax figures,
          and financial regulations change frequently. We make no warranty, express or implied,
          regarding the completeness, accuracy, reliability, or timeliness of any content on this
          Site. See our{" "}
          <Link title="Read the LifeWestWay disclaimer" to="/disclaimer" className="text-accent hover:underline">
            Disclaimer
          </Link>{" "}
          for more detail.
        </p>

        {/* 8. Illustrative Content */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          8. Illustrative Content
        </h2>
        <p>
          Certain articles include composite, illustrative case studies and examples clearly marked
          as such. These do not represent real, identifiable individuals unless explicitly stated.
        </p>

        {/* 9. Limitation of Liability */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          9. Limitation of Liability
        </h2>
        <p>
          To the fullest extent permitted by applicable law, LifeWestWay and its owners, authors, and
          contributors shall not be liable for any direct, indirect, incidental, special,
          consequential, or punitive damages arising from your access to, use of, or inability to
          use this Site, or reliance on any content contained within it.
        </p>

        {/* 10. Indemnification */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          10. Indemnification
        </h2>
        <p>
          You agree to indemnify and hold harmless LifeWestWay, its owners, authors, and affiliates
          from any claims, damages, losses, liabilities, and expenses (including legal fees) arising
          from your use of the Site or violation of these Terms.
        </p>

        {/* 11. Changes to These Terms */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          11. Changes to These Terms
        </h2>
        <p>
          We may revise these Terms at any time by updating this page. Continued use of the Site
          after changes are posted constitutes your acceptance of the revised Terms. We encourage you
          to review this page periodically.
        </p>

        {/* 12. Termination */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">12. Termination</h2>
        <p>
          We reserve the right to restrict or terminate your access to the Site at our discretion,
          without notice, for conduct that we believe violates these Terms or is otherwise harmful to
          other users, us, or third parties.
        </p>

        {/* 13. Governing Law */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
          13. Governing Law
        </h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the
          jurisdiction in which LifeWestWay operates, without regard to its conflict of law
          provisions.
        </p>

        {/* 14. Contact Us */}
        <h2 className="mt-10 font-display text-2xl font-bold text-foreground">14. Contact Us</h2>
        <p>
          If you have questions about these Terms of Use, please visit our{" "}
          <Link title="Contact the LifeWestWay editorial team" to="/contact" className="text-accent hover:underline">
            Contact Us
          </Link>{" "}
          page.
        </p>

        <p className="mt-8 text-sm leading-6 text-muted-foreground">
          This Terms of Use page is provided as a general template suitable for an informational
          content website using Google AdSense. It is not a substitute for legal advice — we
          recommend having these Terms reviewed by a qualified attorney familiar with the laws
          applicable to your business and audience.
        </p>
      </div>

      <nav aria-label="Breadcrumb" className="mt-14 text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <span>Terms of Use</span>
      </nav>
    </div>
  );
}
